import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { BarChart3, TrendingUp, Award, Calculator } from "lucide-react";

const COLORS = ["#10b981", "#8b5cf6", "#0ea5e9", "#f59e0b", "#ec4899", "#f97316", "#06b6d4", "#84cc16", "#e11d48", "#6366f1"];

const fmt = (v) => typeof v === "number" ? `$${(v / 1000).toFixed(0)}k` : v;
const fmtM = (v) => typeof v === "number" ? `$${(v / 1_000_000).toFixed(1)}M` : v;

// Static chart configs extracted to avoid re-renders
const CHART_MARGIN = { top: 5, right: 20, bottom: 5, left: 0 };
const CHART_TICK = { fontSize: 11 };
const CHART_GRID = { strokeDasharray: "3 3", stroke: "#e2e8f0" };
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8 };
const LEGEND_STYLE = { fontSize: 11 };
const BAR_RADIUS = [4, 4, 0, 0];
const DOT_STYLE = { r: 3 };

export default function AdvancedAnalytics({ simId }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendMetric, setTrendMetric] = useState("net_profit");

  useEffect(() => {
    if (!simId) return;
    setLoading(true);
    axios.get(`${API}/analytics/simulations/${simId}/advanced`)
      .then(res => { setData(res.data); })
      .catch(() => { setData(null); })
      .finally(() => { setLoading(false); });
  }, [simId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data || !data.comparison?.length) {
    return (
      <Card><CardContent className="p-12 text-center text-slate-400">
        {t("analytics.noData")}
      </CardContent></Card>
    );
  }

  const { comparison, ranking_evolution, trends, summary_stats, hospital_names } = data;

  const TREND_OPTIONS = [
    { value: "net_profit", label: t("hospital.netProfit") },
    { value: "valuation", label: t("hospital.valuation") },
    { value: "bed_occupancy", label: t("hospital.bedOccupancy") },
    { value: "patient_satisfaction", label: t("hospital.patientSat") },
    { value: "reputation", label: t("hospital.reputation") },
  ];

  const STAT_METRICS = [
    { key: "cumulative_profit", label: t("hospital.cumProfit"), format: fmt },
    { key: "valuation", label: t("hospital.valuation"), format: fmtM },
    { key: "reputation", label: t("hospital.reputation"), format: v => v },
    { key: "patient_satisfaction", label: t("hospital.patientSat"), format: v => v },
    { key: "bed_occupancy", label: t("hospital.bedOccupancy"), format: v => `${v}%` },
    { key: "safety_rating", label: t("hospital.safety"), format: v => v },
    { key: "market_share", label: t("hospital.marketShare"), format: v => `${v}%` },
  ];

  const trendData = trends[trendMetric] || [];
  const isCurrency = trendMetric === "net_profit" || trendMetric === "valuation";

  return (
    <div className="space-y-6" data-testid="advanced-analytics">
      {/* 1. HOSPITAL COMPARISON BAR CHART */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-emerald-500" />
            {t("analytics.comparison")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={comparison} margin={CHART_MARGIN}>
              <CartesianGrid {...CHART_GRID} />
              <XAxis dataKey="name" tick={CHART_TICK} />
              <YAxis tickFormatter={fmt} tick={CHART_TICK} />
              <Tooltip
                formatter={(value, name) => [typeof value === "number" && Math.abs(value) > 10000 ? fmt(value) : value, name]}
                contentStyle={TOOLTIP_STYLE}
              />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Bar dataKey="cumulative_profit" name={t("hospital.cumProfit")} fill="#10b981" radius={BAR_RADIUS} />
              <Bar dataKey="reputation" name={t("hospital.reputation")} fill="#8b5cf6" radius={BAR_RADIUS} />
              <Bar dataKey="patient_satisfaction" name={t("hospital.patientSat")} fill="#0ea5e9" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* 2. RANKING EVOLUTION */}
      {ranking_evolution.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-500" />
              {t("analytics.rankingEvolution")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={ranking_evolution}>
                <CartesianGrid {...CHART_GRID} />
                <XAxis dataKey="round" tick={CHART_TICK} tickFormatter={v => `${t("common.round")} ${v}`} />
                <YAxis reversed domain={[1, hospital_names.length]} tick={CHART_TICK} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={v => `${t("common.round")} ${v}`} />
                <Legend wrapperStyle={LEGEND_STYLE} />
                {hospital_names.map((name, i) => (
                  <Line key={name} type="monotone" dataKey={name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={DOT_STYLE} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* 3. TRENDS */}
      {trendData.length > 0 && (
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              {t("analytics.trends")}
            </CardTitle>
            <Select value={trendMetric} onValueChange={setTrendMetric}>
              <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="trend-metric-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TREND_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={trendData}>
                <CartesianGrid {...CHART_GRID} />
                <XAxis dataKey="round" tick={CHART_TICK} tickFormatter={v => `${t("common.round")} ${v}`} />
                <YAxis tickFormatter={isCurrency ? fmt : v => v} tick={CHART_TICK} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  labelFormatter={v => `${t("common.round")} ${v}`}
                  formatter={(v) => [isCurrency ? fmt(v) : v]}
                />
                <Legend wrapperStyle={LEGEND_STYLE} />
                {hospital_names.map((name, i) => (
                  <Line key={name} type="monotone" dataKey={name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={DOT_STYLE} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* 4. SUMMARY STATISTICS TABLE */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Calculator className="w-4 h-4 text-slate-500" />
            {t("analytics.summaryStats")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("analytics.metric")}</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">{t("analytics.min")}</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">{t("analytics.max")}</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">{t("analytics.mean")}</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">{t("analytics.std")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {STAT_METRICS.map(m => {
                const s = summary_stats[m.key] || {};
                return (
                  <TableRow key={m.key}>
                    <TableCell className="font-medium text-sm">{m.label}</TableCell>
                    <TableCell className="text-right text-sm font-mono tabular-nums">{m.format(s.min)}</TableCell>
                    <TableCell className="text-right text-sm font-mono tabular-nums">{m.format(s.max)}</TableCell>
                    <TableCell className="text-right text-sm font-mono tabular-nums font-medium">{m.format(s.mean)}</TableCell>
                    <TableCell className="text-right text-sm font-mono tabular-nums text-slate-400">{m.format(s.std)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
